import { Link, useParams } from '@tanstack/react-router';
import { Construction, ReceiptText } from 'lucide-react';
import { NOT_BUILT_BLOCK, hubOfModule, type ModuleId, type NotBuiltId } from '../../app/shell/hubs';
import { useI18n } from '../../i18n/I18nProvider';
import { Badge } from '../../ui/Feedback';
import { Page, PageHeader, Panel, useDocumentTitle } from '../../ui/Layout';
import styles from './NotBuilt.module.css';

/**
 * "Not built yet" (council UX decision 2026-10-04; IA rule 1): the page's job in two lines and its
 * planned block. Calm and factual: no disabled controls, no fake actions, no dates or percentages,
 * and at most one link to a page that already works.
 */
export function NotBuiltPanel({ id }: { id: NotBuiltId }) {
  const { t } = useI18n();
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const copy = t.modules[id];
  return (
    <div className={styles.panel} data-testid={`not-built-${id}`} data-block={NOT_BUILT_BLOCK[id]}>
      <span className={styles.tile} aria-hidden="true">
        <Construction size={20} />
      </span>
      <div className={styles.body}>
        <p className={styles.heading}>
          <strong>{copy.name}</strong>
          <Badge tone="accent">{t.notBuilt.chip}</Badge>
        </p>
        <p className={styles.line}>{copy.line1}</p>
        <p className={styles.line}>{copy.line2}</p>
        <p className={styles.block} data-testid="not-built-block">
          {t.notBuilt.plannedBlock(String(NOT_BUILT_BLOCK[id]))}
        </p>
        {id !== 'billingDocuments' && orgId ? (
          <Link to="/orgs/$orgId/sales/invoices" params={{ orgId }} className={styles.link}>
            <ReceiptText size={16} aria-hidden="true" />
            {t.notBuilt.goInvoices}
          </Link>
        ) : null}
      </div>
    </div>
  );
}

export function NotBuiltPage({ id }: { id: ModuleId }) {
  const { t } = useI18n();
  useDocumentTitle(t.modules[id].name);
  return (
    <Page narrow>
      <PageHeader eyebrow={t.nav[hubOfModule(id)]} title={t.modules[id].name} />
      <Panel title={t.notBuilt.whatItWillDo}>
        <NotBuiltPanel id={id} />
      </Panel>
    </Page>
  );
}
